<template>
  <div>
    <div class="d-flex flex-wrap align-center ga-3 mb-4">
      <v-text-field
        v-model="search"
        :label="$t('ncrm.integrations.search')"
        hide-details
        prepend-inner-icon="mdi-magnify"
        class="ncrm-search"
      />
      <v-select
        v-model="selectedSystem"
        :items="systemOptions"
        :label="$t('ncrm.integrations.filters.system')"
        hide-details
        clearable
      />
      <v-select
        v-model="selectedSource"
        :items="sourceOptions"
        :label="$t('ncrm.integrations.filters.source')"
        hide-details
        clearable
      />
      <v-spacer />
      <v-btn color="primary" @click="handleStartImport">
        <v-icon start>mdi-upload</v-icon>
        {{ $t("ncrm.integrations.startImport") }}
      </v-btn>
    </div>

    <!-- DPIA A3 / B3: expunerea la colectarea indirectă, arătată acolo unde
         operatorul se uită oricum la importuri. Apare doar când există. -->
    <v-alert
      v-if="uninformedContacts > 0"
      type="warning"
      density="comfortable"
      class="mb-4"
    >
      {{ $t("ncrm.integrations.uninformed.title", { count: uninformedContacts }) }}
      <div class="text-body-2 mt-1">
        {{ $t("ncrm.integrations.uninformed.hint") }}
      </div>
    </v-alert>

    <v-data-table-server
      :headers="headers"
      :items="filteredImports"
      :items-length="totalItems"
      item-key="id"
      class="elevation-0 ncrm-integrations__table"
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      :loading="tableLoading"
      density="comfortable"
      @click:row="handleRowClick"
    >
      <template #item.status="{ item }">
        <v-chip size="small" :color="statusColor(item.status)" :variant="statusVariant(item.status)">
          {{ formatStatus(item.status) }}
        </v-chip>
      </template>
      <template #no-data>
        <v-alert type="info" class="ma-4">
          {{ $t("ncrm.integrations.empty") }}
        </v-alert>
      </template>
    </v-data-table-server>
  </div>
</template>

<script>
import axios from "axios";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

export default {
  name: "NcrmFileImportsTab",
  emits: ["notify"],
  data() {
    return {
      search: "",
      selectedSystem: null,
      selectedSource: null,
      page: 1,
      itemsPerPage: 25,
      totalItems: 0,
      tableLoading: false,
      imports: [],
      uninformedContacts: 0,
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t("ncrm.integrations.headers.importName"), key: "importName" },
        { title: this.$t("ncrm.integrations.headers.type"), key: "type" },
        { title: this.$t("ncrm.integrations.headers.newRecords"), key: "newRecords" },
        { title: this.$t("ncrm.integrations.headers.updatedRecords"), key: "updatedRecords" },
        { title: this.$t("ncrm.integrations.headers.errors"), key: "errors" },
        { title: this.$t("ncrm.integrations.headers.system"), key: "system" },
        { title: this.$t("ncrm.integrations.headers.source"), key: "source" },
        { title: this.$t("ncrm.integrations.headers.createdBy"), key: "createdBy" },
        { title: this.$t("ncrm.integrations.headers.createdAt"), key: "createdAt" },
        { title: this.$t("ncrm.integrations.headers.status"), key: "status" },
      ];
    },
    systemOptions() {
      return [this.$t("ncrm.integrations.system.crmImport")];
    },
    sourceOptions() {
      return [this.$t("ncrm.integrations.source.ui")];
    },
    filteredImports() {
      return this.imports;
    },
  },
  watch: {
    page() {
      this.fetchImports();
    },
    itemsPerPage() {
      this.page = 1;
      this.fetchImports();
    },
    search() {
      this.page = 1;
      this.fetchImports();
    },
    selectedSystem() {
      this.page = 1;
      this.fetchImports();
    },
    selectedSource() {
      this.page = 1;
      this.fetchImports();
    },
  },
  mounted() {
    this.fetchImports();
    this.fetchUninformedExposure();
  },
  methods: {
    statusMeta(status) {
      if (!status) return { color: "neutral", variant: "tonal" };
      const value = String(status).toLowerCase();
      if (value.includes("rolled_back")) return { color: "error", variant: "flat" };
      if (value.includes("error")) return { color: "error", variant: "tonal" };
      if (value.includes("complete")) return { color: "success", variant: "tonal" };
      if (value.includes("running")) return { color: "warning", variant: "tonal" };
      return { color: "neutral", variant: "tonal" };
    },
    statusColor(status) {
      return this.statusMeta(status).color;
    },
    statusVariant(status) {
      return this.statusMeta(status).variant;
    },
    formatStatus(status) {
      if (!status) return "-";
      return String(status)
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },
    handleStartImport() {
      this.$router.push("/ncrm/contacts");
    },
    // DPIA A3 / B3. Eșecul este tăcut deliberat: indicatorul nu are voie să
    // rupă lista de importuri, iar un rol fără acces la registrul Privacy pur
    // și simplu nu îl vede.
    async fetchUninformedExposure() {
      try {
        const { data } = await axios.get("/ncrm/privacy/import-exposure");
        this.uninformedContacts = Number(data?.total) || 0;
      } catch (_) {
        this.uninformedContacts = 0;
      }
    },
    async fetchImports() {
      this.tableLoading = true;
      try {
        const query = this.search.trim().toLowerCase();
        const hasLocalFilter = Boolean(query || this.selectedSystem || this.selectedSource);
        const requestPage = hasLocalFilter ? 1 : this.page;
        const requestPerPage = hasLocalFilter ? 1000 : this.itemsPerPage;

        const { data } = await axios.get("/ncrm/import/batches", {
          params: {
            page: requestPage,
            perPage: requestPerPage,
          },
        });
        const items = Array.isArray(data?.items) ? data.items : [];
        const normalized = items.map((item) => ({
          id: item.id,
          importName: item.import_name || item.file_name || `Batch #${item.id}`,
          type: item.import_type || "-",
          newRecords: item.success_count ?? 0,
          updatedRecords: "-",
          errors: item.error_count ?? 0,
          system: this.$t("ncrm.integrations.system.crmImport"),
          source: this.$t("ncrm.integrations.source.ui"),
          createdBy: item.created_by_user_id ? `#${item.created_by_user_id}` : "-",
          createdAt: item.created_at ? formatDisplayDateTime(item.created_at) : "-",
          status: item.status || "-",
        }));

        const filtered = normalized.filter((item) => {
          if (this.selectedSystem && item.system !== this.selectedSystem) return false;
          if (this.selectedSource && item.source !== this.selectedSource) return false;
          if (!query) return true;
          return Object.values(item).some((value) =>
            String(value ?? "")
              .toLowerCase()
              .includes(query)
          );
        });

        this.imports = filtered;
        this.totalItems = hasLocalFilter ? filtered.length : Number(data?.total || filtered.length);
      } catch (error) {
        this.$emit("notify", {
          message: error?.response?.data?.error || this.$t("ncrm.integrations.loadError"),
          color: "error",
        });
      } finally {
        this.tableLoading = false;
      }
    },
    handleRowClick(_, row) {
      const item = row?.item || row;
      if (!item?.id) return;
      this.$router.push(`/ncrm/data-integrations/${item.id}`);
    },
  },
};
</script>

<style scoped>
.ncrm-search {
  width: min(100%, 320px);
}
</style>
